import React, { useEffect, useMemo, useState } from 'react';
import { BaseComponentProps } from '../types';
import useAppCookies, { Cookies } from '../hooks/utility/useAppCookies';
import { LoginRequestParams, loadProfile, logIn } from '../services/api/AuthService';
import { AgentData } from '../types/agent';
import useNotification from '../hooks/context/useNotification';
import * as Sentry from '@sentry/react';
import moment from 'moment-timezone';

export type AuthContextContextType = {
  loading: boolean;
  isLoggedIn: boolean;
  user: any;
  logIn: (data: LoginRequestParams) => Promise<void>;
  logOut: () => void;
};

export const AuthContext = React.createContext<AuthContextContextType>(null);

export default function AuthProvider({ children }: BaseComponentProps) {
  const { getCookie, setCookie, removeCookie } = useAppCookies();
  const { notificationApi } = useNotification();

  const token = getCookie(Cookies.AuthToken);
  const [user, setUser] = useState<AgentData>(null);
  const [loading, setLoading] = useState(Boolean(token));

  useEffect(() => {
    Boolean(token) &&
      setCookie(Cookies.AuthToken, token, {
        expires: moment().add(399, 'days').toDate(),
      });
  }, []);

  useEffect(() => {
    const onError = (error: Error) => {
      notificationApi.error({ message: 'Failed to log in' });
      console.error('Failed to log in', { error });
    };

    Boolean(token) &&
      loadProfile()
        .then(({ data }) => setUser(data))
        .catch(onError)
        .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!import.meta.env.DEV) {
      Sentry.getCurrentScope().setUser({
        id: user?.id,
        email: user?.email,
        type: 'occ_agent',
      });
    }
  }, [user]);

  const onLogIn = (data: LoginRequestParams): Promise<void> => {
    setLoading(true);
    return logIn(data)
      .then(({ data }) => {
        setCookie(Cookies.AuthToken, data.access_token, {
          expires: moment().add(399, 'days').toDate(),
        });

        return data.access_token;
      })
      .then(() => loadProfile())
      .then(({ data }) => setUser(data))
      .finally(() => setLoading(false));
  };

  function onLogOut() {
    removeCookie(Cookies.AuthToken);
    setUser(null);
  }

  const context = useMemo(
    () => ({
      loading,
      isLoggedIn: !!token,
      user,
      logIn: onLogIn,
      logOut: onLogOut,
    }),
    [loading, token, user],
  );

  return <AuthContext.Provider value={context}>{children}</AuthContext.Provider>;
}
